import AdminAnalytics from './AdminAnalytics';
import AdminSettings from './AdminSettings';
import { blogImages } from '../../lib/blogData';
import { BLOG_TOPICS } from '../../lib/blogTopics';
import { announceBlogUpdate } from '../../lib/blogData';
import React, { useState, useEffect, useCallback, useRef } from "react";
import slugify from "../../_helpers/slugify";
import "./Admin.scss";
import supabase from "../../lib/supabaseClient";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ProjectForm } from "./ProjectsSection";

// ─── Constants ───────────────────────────────────────────────
const STORAGE_BUCKET = "blog-images";
const TABLE_NAME = "blogs";

// ─── Toast Helpers ───────────────────────────────────────────
const notify = {
  success: (msg) => toast.success(msg, { theme: "dark" }),
  error: (msg) => toast.error(msg, { theme: "dark" }),
  info: (msg) => toast.info(msg, { theme: "dark" }),
};

// ─── Sub-components ──────────────────────────────────────────
const Spinner = () => <div className="admin-spinner" />;

// ★ Loading Overlay — بلور + اسپینر
const LoadingOverlay = ({ message = "در حال پردازش..." }) => (
  <div className="loading-overlay">
    <div className="loading-overlay-content">
      <div className="overlay-spinner" />
      <p className="overlay-message">{message}</p>
    </div>
  </div>
);

// ─── Login Form ──────────────────────────────────────────────
const LoginForm = ({ onLogin, loading, error }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  return (
    <div className="admin-login-wrapper">
      <div className="admin-login-card">
        <div className="login-header">
          <div className="login-logo">K</div>
          <h1>پنل مدیریت</h1>
          <p>Kavak Architects</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="email">ایمیل</label>
            <input
              id="email"
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">رمز عبور</label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn-gold" disabled={loading}>
            {loading ? <Spinner /> : "ورود"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─── Sidebar ─────────────────────────────────────────────────
const Sidebar = ({ activeSection, setActiveSection, onLogout, userEmail }) => {
  const sections = [
    { key: "dashboard", label: "داشبورد", icon: "📊" },
    { key: "analytics", label: "آمار بازدید", icon: "📈" },
    { key: "blogs", label: "مدیریت مقالات", icon: "📝" },
    { id: "projects", label: "مدیریت پروژه‌ها", icon: "🏗️" },
    { key: "settings", label: "تنظیمات", icon: "⚙️" },
  ];

  return (
    <aside className="admin-sidebar">
      <div className="sidebar-brand">
        <div className="brand-logo">K</div>
        <span>Kavak Admin</span>
      </div>

      <nav className="sidebar-nav">
        {sections.map((s) => (
          <button
            key={s.key || s.id}
            className={`nav-item ${activeSection === s.key ? "active" : ""}`}
            onClick={() => setActiveSection(s.key)}
          >
            <span className="nav-icon">{s.icon}</span>
            <span className="nav-label">{s.label}</span>
          </button>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">
            {userEmail?.charAt(0).toUpperCase()}
          </div>
          <span className="user-email" title={userEmail}>
            {userEmail}
          </span>
        </div>
        <button className="btn-logout" onClick={onLogout}>
          خروج
        </button>
      </div>
    </aside>
  );
};

// ─── Dashboard Section ───────────────────────────────────────
const DashboardSection = ({ blogs }) => {
  const published = blogs.filter((b) => b.is_published).length;
  const drafts = blogs.filter((b) => !b.is_published).length;

  return (
    <div className="section-dashboard">
      <h2 className="section-title">داشبورد</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📄</div>
          <div className="stat-info">
            <span className="stat-number">{blogs.length}</span>
            <span className="stat-label">کل مقالات</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-number">{published}</span>
            <span className="stat-label">منتشر شده</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <span className="stat-number">{drafts}</span>
            <span className="stat-label">پیش‌نویس</span>
          </div>
        </div>
      </div>

      <div className="recent-posts">
        <h3>آخرین مقالات</h3>
        {blogs.length === 0 ? (
          <div className="empty-state-mini">
            <p>هنوز مقاله‌ای ثبت نشده است</p>
          </div>
        ) : (
          blogs.slice(0, 5).map((b) => (
            <div key={b.id} className="recent-post-row">
              <span className="recent-title">{b.title_fa || b.title_en}</span>
              <span
                className={`badge ${b.is_published ? "published" : "draft"}`}
              >
                {b.is_published ? "منتشر شده" : "پیش‌نویس"}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Image Preview Item ──────────────────────────────────────
const ImagePreviewItem = ({ image, index, onRemove, onReplace }) => {
  const replaceInputRef = useRef(null);

  const handleReplaceClick = () => {
    replaceInputRef.current?.click();
  };

  const handleReplaceFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      onReplace(index, file);
    }
    e.target.value = "";
  };

  return (
    <div className="image-preview-item">
      <div className="image-preview-container">
        <img src={image.preview} alt={`preview-${index}`} />
      </div>
      <div className="image-preview-actions">
        <button
          type="button"
          className="btn-img-action btn-img-replace"
          onClick={handleReplaceClick}
          title="جایگزینی"
        >
          🔄
        </button>
        <button
          type="button"
          className="btn-img-action btn-img-delete"
          onClick={() => onRemove(index)}
          title="حذف"
        >
          🗑️
        </button>
      </div>
      <span className="image-index">
        {index + 1}
        {image.isExisting && <span className="existing-badge">موجود</span>}
      </span>
      <input
        type="file"
        accept="image/*"
        ref={replaceInputRef}
        onChange={handleReplaceFile}
        style={{ display: "none" }}
      />
    </div>
  );
};

// ─── Blog Form ───────────────────────────────────────────────
const emptyForm = {
  topic: "",
  title_fa: "",
  title_en: "",
  description_fa: "",
  description_en: "",
  is_published: false,
};

const BlogForm = ({ initial, defaultTopic, onSubmit, onCancel, loading }) => {
  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const fileInputRef = useRef(null);

  // Initialize form when editing
  useEffect(() => {
    if (initial) {
      setForm({
        topic: initial.topic || "",
        title_fa: initial.title_fa || "",
        title_en: initial.title_en || "",
        description_fa: initial.description_fa || "",
        description_en: initial.description_en || "",
        is_published: initial.is_published || false,
      });

      const existingUrls = blogImages(initial);
      const urls =
        existingUrls.length > 0
          ? existingUrls
          : initial.image_url
          ? [initial.image_url]
          : [];

      setImages(
        urls.map((url) => ({
          file: null,
          preview: url,
          isExisting: true,
          existingUrl: url,
        })),
      );
    } else {
      setForm({ ...emptyForm, topic: defaultTopic || "" });
      setImages([]);
    }
    setErrors({});
    setTouched({});
  }, [initial, defaultTopic]);

  // Validation
  const requiredFields = {
    topic: "موضوع مقاله",
    title_fa: "عنوان فارسی",
    title_en: "عنوان انگلیسی",
  };

  const validate = (formData) => {
    const newErrors = {};
    Object.keys(requiredFields).forEach((key) => {
      if (!formData[key] || formData[key].trim() === "") {
        newErrors[key] = `${requiredFields[key]} الزامی است`;
      }
    });
    return newErrors;
  };

  useEffect(() => {
    const newErrors = validate(form);
    setErrors(newErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  // Multi-Image Handlers
  const handleAddImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newImages = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
      existingUrl: null,
    }));

    setImages((prev) => [...prev, ...newImages]);
    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const updated = [...prev];
      if (!updated[index].isExisting) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleReplaceImage = (index, newFile) => {
    setImages((prev) => {
      const updated = [...prev];
      if (!updated[index].isExisting) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated[index] = {
        file: newFile,
        preview: URL.createObjectURL(newFile),
        isExisting: false,
        existingUrl: null,
      };
      return updated;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allTouched = {};
    Object.keys(requiredFields).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    const finalErrors = validate(form);
    setErrors(finalErrors);

    if (Object.keys(finalErrors).length > 0) {
      const firstErrorField = Object.keys(finalErrors)[0];
      const el = document.querySelector(`[name="${firstErrorField}"]`);
      if (el) el.focus();
      notify.error("لطفاً فیلدهای اجباری را پر کنید");
      return;
    }

    onSubmit(form, images);
  };

  const getFieldClassName = (fieldName) => {
    if (touched[fieldName] && errors[fieldName]) {
      return "has-error";
    }
    return "";
  };

  return (
    <form className="blog-form" onSubmit={handleSubmit} noValidate>
      <div className="form-grid">
        {/* عنوان فارسی */}
        <div className={`form-group ${getFieldClassName("title_fa")}`}>
          <label>
            عنوان فارسی <span className="required-star">*</span>
          </label>
          <input
            name="title_fa"
            value={form.title_fa}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="عنوان مقاله به فارسی"
            dir="rtl"
          />
          {touched.title_fa && errors.title_fa && (
            <span className="field-error">{errors.title_fa}</span>
          )}
        </div>

        <div className="form-group"><label htmlFor="article-topic">موضوع مقاله</label><select id="article-topic" name="topic" value={form.topic} onChange={handleChange} onBlur={handleBlur} required><option value="">انتخاب موضوع</option>{BLOG_TOPICS.map(topic => <option key={topic.value} value={topic.value}>{topic.value}</option>)}</select>{touched.topic && errors.topic && <span className="field-error">{errors.topic}</span>}</div>
        {/* عنوان انگلیسی */}
        <div className={`form-group ${getFieldClassName("title_en")}`}>
          <label>
            عنوان انگلیسی <span className="required-star">*</span>
          </label>
          <input
            name="title_en"
            value={form.title_en}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Article title in English"
            dir="ltr"
          />
          {touched.title_en && errors.title_en && (
            <span className="field-error">{errors.title_en}</span>
          )}
        </div>

        {/* توضیحات فارسی */}
        <div className="form-group full-width">
          <label>توضیحات فارسی</label>
          <textarea
            name="description_fa"
            value={form.description_fa}
            onChange={handleChange}
            rows={5}
            placeholder="متن مقاله به فارسی..."
            dir="rtl"
          />
        </div>

        {/* توضیحات انگلیسی */}
        <div className="form-group full-width">
          <label>توضیحات انگلیسی</label>
          <textarea
            name="description_en"
            value={form.description_en}
            onChange={handleChange}
            rows={5}
            placeholder="Article content in English..."
            dir="ltr"
          />
        </div>

        {/* آپلود تصاویر */}
        <div className="form-group full-width">
          <label>تصاویر مقاله</label>
          <div
            className="multi-image-upload"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              accept="image/*"
              multiple
              ref={fileInputRef}
              onChange={handleAddImages}
              style={{ display: "none" }}
            />
            <div className="upload-zone">
              <span className="upload-icon">📁</span>
              <span className="upload-text">کلیک کنید یا فایل‌ها را بکشید</span>
              <span className="upload-hint">
                می‌توانید چند عکس همزمان انتخاب کنید
              </span>
            </div>
          </div>
        </div>

        {/* پیش‌نمایش تصاویر */}
        {images.length > 0 && (
          <div className="form-group full-width">
            <label>پیش‌نمایش تصاویر ({images.length} عکس)</label>
            <div className="images-preview-grid">
              {images.map((img, idx) => (
                <ImagePreviewItem
                  key={`${idx}-${img.preview}`}
                  image={img}
                  index={idx}
                  onRemove={handleRemoveImage}
                  onReplace={handleReplaceImage}
                />
              ))}
            </div>
          </div>
        )}

        {/* انتشار فوری */}
        <div className="form-group full-width">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="is_published"
              checked={form.is_published}
              onChange={handleChange}
            />
            <span className="checkmark" />
            انتشار فوری
          </label>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-gold" disabled={loading}>
          {loading ? <Spinner /> : initial ? "بروزرسانی" : "ذخیره مقاله"}
        </button>
        {onCancel && (
          <button type="button" className="btn-outline" onClick={onCancel}>
            انصراف
          </button>
        )}
      </div>
    </form>
  );
};

// ─── Blogs Section ───────────────────────────────────────────
const BlogsSection = ({
  blogs,
  onCreateOrUpdate,
  onDelete,
  onTogglePublish,
  loading,
}) => {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [topicFilter, setTopicFilter] = useState("");
  const visibleBlogs = topicFilter ? blogs.filter(blog => blog.topic === topicFilter) : blogs;

  const handleSubmit = async (form, images) => {
    const saved = await onCreateOrUpdate(form, images, editing?.id || null);
    if (!saved) return;
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (blog) => {
    setEditing(blog);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("آیا از حذف این مقاله مطمئن هستید؟")) {
      setDeletingId(id);
      await onDelete(id);
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setEditing(null);
    setShowForm(false);
  };

  const getThumb = blog => blogImages(blog)[0] || null;
  const getImageCount = blog => blogImages(blog).length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("fa-IR");
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="section-blogs">
      <div className="section-header">
        <h2 className="section-title">مدیریت مقالات</h2>
        <select aria-label="فیلتر موضوع مقالات" value={topicFilter} onChange={e => setTopicFilter(e.target.value)}><option value="">همه‌ی موضوع‌ها</option>{BLOG_TOPICS.map(topic => <option key={topic.value} value={topic.value}>{topic.value}</option>)}</select>
        {!showForm && (
          <button
            className="btn-gold"
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
          >
            ➕ مقاله جدید
          </button>
        )}
      </div>

      {/* فرم ایجاد/ویرایش */}
      {showForm && (
        <div className="form-card">
          <h3>{editing ? "✏️ ویرایش مقاله" : "📝 مقاله جدید"}</h3>
          <BlogForm
            initial={editing}
            defaultTopic={topicFilter}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </div>
      )}

      {/* جدول مقالات */}
      {visibleBlogs.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">📄</span>
          <p>هنوز مقاله‌ای ثبت نشده است</p>
        </div>
      ) : (
        <div className="blog-table-wrapper">
          <table className="blog-table">
            <thead>
              <tr>
                <th>تصویر</th>
                <th>عنوان</th>
                <th>تعداد عکس</th>
                <th>وضعیت</th>
                <th>تاریخ</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {visibleBlogs.map((blog) => {
                const thumb = getThumb(blog);
                const imgCount = getImageCount(blog);

                return (
                  <tr key={blog.id}>
                    <td data-label="تصویر">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={blog.title_fa || blog.title_en || "تصویر مقاله"}
                          className="table-thumb"
                          loading="lazy"
                        />
                      ) : (
                        <div className="table-thumb-placeholder">🖼️</div>
                      )}
                    </td>
                    <td data-label="عنوان"><strong>{blog.topic}</strong><br />{blog.title_fa || blog.title_en || "بدون عنوان"}</td>
                    <td data-label="تعداد عکس">
                      {imgCount > 0 ? (
                        <span className="image-count-badge">🖼️ {imgCount}</span>
                      ) : (
                        <span style={{ color: "#666" }}>—</span>
                      )}
                    </td>
                    <td data-label="وضعیت">
                      <span
                        className={`badge ${
                          blog.is_published ? "published" : "draft"
                        }`}
                      >
                        {blog.is_published ? "منتشر شده" : "پیش‌نویس"}
                      </span>
                    </td>
                    <td data-label="تاریخ" className="date-cell">{formatDate(blog.created_at)}</td>
                    <td data-label="عملیات">
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(blog)}
                          title="ویرایش"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon btn-publish"
                          onClick={() => onTogglePublish(blog)}
                          title={blog.is_published ? "پنهان کردن" : "انتشار"}
                        >
                          {blog.is_published ? "🔒" : "🌐"}
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(blog.id)}
                          disabled={deletingId === blog.id}
                          title="حذف"
                        >
                          {deletingId === blog.id ? <Spinner /> : "🗑️"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---- PROJECTS SECTION -------

const ProjectsSection = ({ projects, onCreateOrUpdate, onDelete, loading }) => {
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleSubmit = async (form, images) => {
    const saved = await onCreateOrUpdate(form, images, editing?.id || null);
    if (!saved) return;
    setShowForm(false);
    setEditing(null);
  };

  const handleEdit = (project) => {
    setEditing(project);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (window.confirm("آیا از حذف این پروژه اطمینان دارید؟")) {
      setDeletingId(id);
      await onDelete(id);
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  return (
    <div className="admin-section projects-section">
      {!showForm && (
        <button className="btn-new" onClick={() => setShowForm(true)}>
          + پروژه جدید
        </button>
      )}

      {showForm && (
        <ProjectForm
          initial={editing}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      )}

      <div className="projects-table">
        <table>
          <thead>
            <tr>
              <th>تصویر</th>
              <th>عنوان فارسی</th>
              <th>عنوان انگلیسی</th>
              <th>منطقه</th>
              <th>سال</th>
              <th>تصاویر</th>
              <th>وضعیت</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id}>
                <td>
                  {project.image_urls?.[0] && (
                    <img
                      src={project.image_urls[0]}
                      alt=""
                      className="table-thumb"
                    />
                  )}
                </td>
                <td>{project.title_fa}</td>
                <td>{project.title_en}</td>
                <td>{project.area_fa}</td>
                <td>{project.year_fa}</td>
                <td>{project.image_urls?.length || 0}</td>
                <td>
                  <span
                    className={`status ${
                      project.is_published ? "published" : "draft"
                    }`}
                  >
                    {project.is_published ? "منتشر شده" : "پیش‌نویس"}
                  </span>
                </td>
                <td className="actions">
                  <button onClick={() => handleEdit(project)}>ویرایش</button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    disabled={deletingId === project.id}
                  >
                    {deletingId === project.id ? "..." : "حذف"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ★★★ MAIN ADMIN COMPONENT ★★★
// ═══════════════════════════════════════════════════════════════
const Admin = () => {
  // ─── State ──────────────────────────────────────────────
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState("");
  const [loginError, setLoginError] = useState("");
  const [adminAllowed, setAdminAllowed] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const [blogs, setBlogs] = useState([]);
  const [activeSection, setActiveSection] = useState("dashboard");

  // ─── Auth ───────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    }).catch(() => { setAuthLoading(false); setLoginError("خطا در اتصال؛ دوباره تلاش کنید."); });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    let active = true;
    setAdminAllowed(false);
    if (!session) { setCheckingAdmin(false); return; }
    setCheckingAdmin(true);
    supabase.from('admin_whitelist').select('id').eq('email', session.user.email).maybeSingle()
      .then(({ data, error }) => { if (active) { setAdminAllowed(Boolean(data) && !error); setCheckingAdmin(false); } })
      .catch(() => { if (active) setCheckingAdmin(false); });
    return () => { active = false; };
  }, [session]);

  // ─── Fetch Blogs ────────────────────────────────────────
  const fetchBlogs = useCallback(async () => {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Fetch error:", error);
      notify.error("خطا در دریافت مقالات");
    } else {
      setBlogs(data || []);
    }
  }, []);

  useEffect(() => {
    if (session && adminAllowed) fetchBlogs();
  }, [session, adminAllowed, fetchBlogs]);

  // ─── Login ──────────────────────────────────────────────
  const handleLogin = async (email, password) => {
    setLoading(true);
    setLoginError("");

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);
    if (error) {
      setLoginError(error.message);
      notify.error("خطا در ورود: " + error.message);
    } else {
      notify.success("ورود موفق ✅");
    }
  };

  // ─── Logout ─────────────────────────────────────────────
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    notify.info("از حساب خارج شدید");
  };

  // ═══════════════════════════════════════════════════════
  // ★★★ UPLOAD IMAGE — نسخه نهایی اصلاح شده ★★★
  // ═══════════════════════════════════════════════════════

  const STORAGE_BUCKET = "blog-images";

  // ★ Supabase URL رو مستقیم هم تعریف کن (backup)
  const SUPABASE_URL =
    supabase.supabaseUrl || "https://ehnorglxlfhcndvofsmc.supabase.co";

  const compressImage = (file) => {
    return new Promise((resolve) => {
      // اگه فایل کمتر از 1MB هست، فشرده نکن
      if (file.size <= 1 * 1024 * 1024) {
        resolve(file);
        return;
      }

      const img = new Image();
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        // حداکثر ابعاد
        const MAX_WIDTH = 1920;
        const MAX_HEIGHT = 1080;
        let { width, height } = img;

        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              resolve(
                new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), {
                  type: "image/webp",
                }),
              );
            } else {
              resolve(file);
            }
          },
          "image/webp",
          0.82,
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImage = async (file) => {
    // مرحله ۱: کمپرس
    const compressed = await compressImage(file);

    // مرحله ۲: نام فایل تمیز
    const fileExt =
      compressed.type === "image/webp"
        ? "webp"
        : file.name
            .split(".")
            .pop()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "") || "jpg";

    const uniqueId = Math.random().toString(36).substring(2, 10);
    const timestamp = Date.now();
    const fileName = `img_${timestamp}_${uniqueId}.${fileExt}`;

    // مرحله ۳: آپلود
    const { data, error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(fileName, compressed, {
        cacheControl: "3600",
        contentType: compressed.type || `image/${fileExt}`,
        upsert: false,
      });

    if (uploadError) {
      console.error("❌ Upload error:", uploadError);

      if (
        uploadError.message?.includes("duplicate") ||
        uploadError.message?.includes("already exists")
      ) {
        const retryName = `img_${timestamp}_${uniqueId}_r.${fileExt}`;
        const { data: retryData, error: retryError } = await supabase.storage
          .from(STORAGE_BUCKET)
          .upload(retryName, compressed, {
            cacheControl: "3600",
            contentType: compressed.type || `image/${fileExt}`,
            upsert: true,
          });

        if (retryError) throw new Error(`آپلود ناموفق: ${retryError.message}`);

        // ★★★ ساخت URL دستی با /public/ ★★★
        const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${retryData.path}`;
        return publicUrl;
      }

      throw new Error(`آپلود ناموفق: ${uploadError.message}`);
    }

    // ★★★ ساخت URL دستی — اینجا کلید حل مشکل هست ★★★
    // بجای استفاده از getPublicUrl که مسیر اشتباه می‌ده
    const publicUrl = `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${data.path}`;

    // ★ تست URL
    try {
      const testResp = await fetch(publicUrl, { method: "HEAD" });
      if (!testResp.ok) {
        console.warn(
          "⚠️ URL returned non-200, but image might still be accessible",
        );
      }
    } catch (e) {
      console.warn("⚠️ URL test failed:", e.message);
    }

    return publicUrl;
  };

  // ═══════════════════════════════════════════════════════
  // ★★★ CREATE / UPDATE BLOG ★★★
  // ═══════════════════════════════════════════════════════
  const handleCreateOrUpdate = async (formData, images, editId) => {
    setLoading(true);

    try {
      // ─── مرحله ۱: آپلود تصاویر جدید ─────────────────
      const totalNew = images.filter((img) => !img.isExisting).length;
      let uploadCount = 0;
      const uploadedUrls = [];

      for (const img of images) {
        if (img.isExisting) {
          // عکس موجود — URL رو نگه دار
          uploadedUrls.push(img.existingUrl);
        } else {
          // عکس جدید — آپلود کن
          uploadCount++;
          setOverlayMessage(
            `در حال آپلود تصویر ${uploadCount} از ${totalNew}...`,
          );

          const url = await uploadImage(img.file);
          uploadedUrls.push(url);
        }
      }

      // ─── مرحله ۲: ذخیره در دیتابیس ─────────────────
      setOverlayMessage("در حال ذخیره مقاله...");

      const existing = editId && blogs.find(blog => blog.id === editId);
      const slug = existing?.slug || `${slugify(formData.title_en?.trim() || formData.title_fa)}-${crypto.randomUUID().slice(0,8)}`;

      const blogData = {
        topic: formData.topic,
        title_fa: formData.title_fa?.trim(),
        title_en: formData.title_en?.trim(),
        description_fa: formData.description_fa?.trim() || "",
        description_en: formData.description_en?.trim() || "",
        is_published: formData.is_published || false,
        slug,
        // ★ آرایه URL ها
        image_urls: uploadedUrls.length > 0 ? uploadedUrls : null,
        // ★ اولین عکس به عنوان عکس اصلی (backward compatibility)
        image_url: uploadedUrls.length > 0 ? uploadedUrls[0] : null,
      };

      let result;

      if (editId) {
        result = await supabase
          .from(TABLE_NAME)
          .update(blogData)
          .eq("id", editId)
          .select();
      } else {
        result = await supabase.from(TABLE_NAME).insert(blogData).select();
      }

      if (!result.error && result.data?.length !== 1) throw new Error("ذخیره انجام نشد؛ دسترسی حساب را بررسی کنید.");
      if (result.error) {
        throw new Error(result.error.message);
      }

      notify.success(editId ? "مقاله بروزرسانی شد ✅" : "مقاله ذخیره شد ✅");
      announceBlogUpdate();
      await fetchBlogs();
      return true;
    } catch (err) {
      console.error("❌ Save error:", err);
      notify.error("خطا: " + err.message);
      return false;
    } finally {
      setLoading(false);
      setOverlayMessage("");
    }
  };

  // ─── Delete Blog ────────────────────────────────────────
  const handleDelete = async (id) => {
    setLoading(true);
    setOverlayMessage("در حال حذف...");

    try {
      const { error } = await supabase.from(TABLE_NAME).delete().eq("id", id);

      if (error) throw new Error(error.message);

      notify.success("مقاله حذف شد 🗑️");
      announceBlogUpdate();
      await fetchBlogs();
      return true;
    } catch (err) {
      notify.error("خطا در حذف: " + err.message);
    } finally {
      setLoading(false);
      setOverlayMessage("");
    }
  };

  // ─── Toggle Publish ─────────────────────────────────────
  const handleTogglePublish = async (blog) => {
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ is_published: !blog.is_published })
        .eq("id", blog.id);

      if (error) throw new Error(error.message);

      notify.success(
        blog.is_published ? "مقاله پنهان شد" : "مقاله منتشر شد ✅",
      );
      announceBlogUpdate();
      await fetchBlogs();
      return true;
    } catch (err) {
      notify.error("خطا: " + err.message);
    }
  };

  // ─── Section Title for Topbar ───────────────────────────
  const getSectionTitle = () => {
    switch (activeSection) {
      case "dashboard":
        return "داشبورد";
      case "analytics":
        return "آمار بازدید";
      case "blogs":
        return "مدیریت مقالات";
      case "projects":
        return "مدیریت پروژه‌ها";
      case "settings":
        return "تنظیمات";
      default:
        return "";
    }
  };

  // ═══════════════════════════════════════════════════════
  // ─── RENDER ─────────────────────────────────────────────
  // ═══════════════════════════════════════════════════════

  // Loading auth state
  if (authLoading || checkingAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <Spinner />
          <p>در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!session) {
    return (
      <div className="admin-page">
        <LoginForm onLogin={handleLogin} loading={loading} error={loginError} />
        <ToastContainer
          position="top-center"
          autoClose={3000}
          rtl
          theme="dark"
        />
      </div>
    );
  }

  if (!adminAllowed) return <div className="admin-page"><p>این حساب اجازه‌ی مدیریت سایت را ندارد. در صورت خطای اتصال، صفحه را دوباره بارگذاری کنید.</p><button onClick={handleLogout}>خروج</button></div>;

  // Logged in — Admin Dashboard
  return (
    <div className="admin-page">
      <div className="admin-layout">
        <Sidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onLogout={handleLogout}
          userEmail={session.user.email}
        />

        <div className="admin-main">
          <div className="admin-topbar">
            <h2 className="topbar-title">{getSectionTitle()}</h2>
            <span className="topbar-date">
              {new Date().toLocaleDateString("fa-IR", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>

          <div className="admin-content">
            {activeSection === "dashboard" && (
              <DashboardSection blogs={blogs} />
            )}

            {activeSection === "blogs" && (
              <BlogsSection
                blogs={blogs}
                onCreateOrUpdate={handleCreateOrUpdate}
                onDelete={handleDelete}
                onTogglePublish={handleTogglePublish}
                loading={loading}
              />
            )}
            {activeSection === "analytics" && <AdminAnalytics />}
            {activeSection === "projects" && <ProjectsSection />}

            {activeSection === "settings" && (
              <AdminSettings user={session.user} />
            )}
          </div>
        </div>
      </div>

      {/* ★ Loading Overlay */}
      {loading && overlayMessage && <LoadingOverlay message={overlayMessage} />}

      {/* ★ Toast Container */}
      <ToastContainer position="top-center" autoClose={3000} rtl theme="dark" />
    </div>
  );
};

export default Admin;
